import { useEffect, useState } from 'react';
import { Modal, Select, DatePicker, message } from 'antd';
import dayjs from 'dayjs';
import { CrUploadCloudIcon, CrDescDocIcon } from '../../assets/icons';
import { useUploadS3Mutation } from '../../features/uploads/uploadApi';
import type { CrAsset } from '../../features/citizenRequests/citizenRequestApi';

/** An assignable sub-task under an issue (Step 3 "Assign Task"). UI-only for now. */
export interface SubTask {
  id: number;
  name: string;
  assigneeId: number | '';
  assigneeName: string;
  dueDate: string; // YYYY-MM-DD
  status: string;
  attachment?: CrAsset;
}

/** Sub-task status options + badge styles (Step 3). */
export const STATUSES: { value: string; badge: string }[] = [
  { value: 'To-Do', badge: 'bg-[#f1f5f9] text-[#475569]' },
  { value: 'In Progress', badge: 'bg-[#eff6ff] text-brand' },
  { value: 'Pending', badge: 'bg-[#fffbeb] text-[#b45309]' },
  { value: 'Completed', badge: 'bg-[#ecfdf5] text-[#047857]' },
];
export const statusBadge = (s: string) =>
  STATUSES.find((x) => x.value === s)?.badge ?? STATUSES[0].badge;

const LABEL = 'text-xs font-medium text-[#334155]';
const INPUT =
  'w-full rounded-md border border-[#e2e8f0] bg-white px-3 py-2.5 text-sm text-ink placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-brand/15';

interface Props {
  open: boolean;
  issueTitle: string;
  memberOptions: { id: number; name: string }[];
  initial?: SubTask | null;
  onCancel: () => void;
  onSave: (sub: SubTask) => void;
}

const empty: SubTask = {
  id: 0,
  name: '',
  assigneeId: '',
  assigneeName: '',
  dueDate: '',
  status: 'To-Do',
  attachment: undefined,
};

/** "Assign Task" modal (Figma 1482-2429) — add/edit a sub-task under an issue. */
export default function AssignTaskModal({
  open,
  issueTitle,
  memberOptions,
  initial,
  onCancel,
  onSave,
}: Props) {
  const [draft, setDraft] = useState<SubTask>(empty);
  const [touched, setTouched] = useState(false);
  const [uploadS3] = useUploadS3Mutation();
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(initial ?? empty);
      setTouched(false);
    }
  }, [open, initial]);

  const set = (patch: Partial<SubTask>) => setDraft((d) => ({ ...d, ...patch }));
  const nameMissing = !draft.name.trim();
  const assigneeMissing = draft.assigneeId === '';
  const dueMissing = !draft.dueDate;
  const canSave = !nameMissing && !assigneeMissing && !dueMissing && !!draft.status;

  const onFile = async (file?: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadS3(file).unwrap();
      set({
        attachment: { file_url: res.url, file_type: res.content_type || file.type, name: file.name },
      });
    } catch {
      message.error('File upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = () => {
    if (!canSave) {
      setTouched(true);
      return;
    }
    onSave({
      ...draft,
      name: draft.name.trim(),
      assigneeName:
        memberOptions.find((m) => m.id === draft.assigneeId)?.name || draft.assigneeName,
    });
  };

  const err = (cond: boolean) => (touched && cond ? 'This field is required' : undefined);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={
        <div className="flex flex-col gap-0.5">
          <span className="text-lg font-semibold text-ink">Assign Task</span>
          <span className="truncate text-xs font-normal text-[#64748b]">
            Assign task details for: {issueTitle}
          </span>
        </div>
      }
      okText={initial ? 'Save Task' : 'Assign Task'}
      onOk={handleSave}
      okButtonProps={{ disabled: !canSave }}
      width={560}
      destroyOnHidden
    >
      <div className="flex flex-col gap-4 py-2">
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>
            Task Name<span className="text-[#ef4444]"> *</span>
          </span>
          <input
            className={[INPUT, err(nameMissing) ? '!border-[#ef4444]' : ''].join(' ')}
            placeholder="e.g. Inspect main distribution valve & repair leakage"
            value={draft.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>
            Assign To<span className="text-[#ef4444]"> *</span>
          </span>
          <Select
            className="w-full"
            placeholder="Select an org user"
            showSearch
            optionFilterProp="label"
            status={err(assigneeMissing) ? 'error' : undefined}
            value={draft.assigneeId === '' ? undefined : draft.assigneeId}
            onChange={(v) => set({ assigneeId: (v as number) ?? '' })}
            options={memberOptions.map((m) => ({ value: m.id, label: m.name }))}
          />
        </label>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className={LABEL}>
              Due Date<span className="text-[#ef4444]"> *</span>
            </span>
            <DatePicker
              className="w-full"
              format="DD MMM YYYY"
              placeholder="Select date"
              status={err(dueMissing) ? 'error' : undefined}
              value={draft.dueDate ? dayjs(draft.dueDate) : null}
              onChange={(d) => set({ dueDate: d ? d.format('YYYY-MM-DD') : '' })}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className={LABEL}>
              Status<span className="text-[#ef4444]"> *</span>
            </span>
            <Select
              className="w-full"
              value={draft.status || undefined}
              onChange={(v) => set({ status: v ?? '' })}
              options={STATUSES.map((s) => ({ value: s.value, label: s.value }))}
            />
          </label>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className={LABEL}>Attachment</span>
          <label
            className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-[#d1e2ff] bg-[#f8faff] p-5 text-center transition-colors hover:border-brand/40"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              onFile(e.dataTransfer.files?.[0]);
            }}
          >
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              className="hidden"
              onChange={(e) => {
                onFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <CrUploadCloudIcon className="h-4 w-[22px] text-brand" />
            <span className="text-xs text-[#334155]">
              {uploading ? 'Uploading…' : (
                <>
                  Drag and drop file here, or <span className="font-semibold text-brand">browse</span>
                </>
              )}
            </span>
            <span className="text-[10px] text-[#94a3b8]">Supports PDF, PNG, JPG up to 10MB</span>
          </label>
          {draft.attachment && (
            <div className="flex items-center justify-between gap-2 rounded-md border border-[#e2e8f0] bg-white px-3 py-2">
              <span className="flex min-w-0 items-center gap-2">
                <CrDescDocIcon className="size-4 shrink-0 text-[#64748b]" />
                <span className="truncate text-xs text-[#334155]">{draft.attachment.name}</span>
              </span>
              <button
                type="button"
                aria-label="Remove attachment"
                onClick={() => set({ attachment: undefined })}
                className="shrink-0 rounded px-1 text-sm leading-none text-[#94a3b8] hover:text-ink"
              >
                ×
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
